import Breadcrumb, { Crumb, Separator } from "@asphalt-react/breadcrumb";
import Button from "@asphalt-react/button";
import Dropdown from "@asphalt-react/dropdown";
import Flag from "@asphalt-react/flag";
import Stack from "@asphalt-react/stack";
import TextField from "@asphalt-react/textfield";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { UserContext } from "../../common/hooks/UserContext";
import routes from "../../common/models/route-constants";
import LoadingPage from "../../common/pages/LoadingPage";
import { arrayOfStringsToArrayOfIdKeyObjects } from "../../common/utils/generalUtils";
import {
  createDefaultIllustration,
  getCombinedName,
  getIllustrationNameComponentCategories,
  getDarkroomStorageUrls,
  uploadFileToBucket,
  isIllustrationNameUnique,
  checkVpnConnection,
  activateBcsForAllAppIds,
} from "../api/illustrationsApi";
import { getRasterImageFromSvg } from "../utils/illustrationTransformationUtils";
import AssetSelector from "../../common/components/AssetSelector";
import {
  getIllustrationNameError,
  validIllustrationName,
} from "../validations/illustrationValidations";
import styles from "./CreateDefaultIllustrationPage.module.css";

/* All user-facing texts on this page */
const texts = {
  breadcrumbAssets: "assets",
  breadcrumbAddIllustration: "add illustration",
  pageHeading: "Add Default Illustration",
  namingRulesTitle: "How to name your illustration",
  namingRulesContent:
    "1. All lowercase characters\n2. No special characters except '_'\n3. No numbers",
  productCategoryLabel: "Product Category",
  illustrationCategoryLabel: "Illustration Category",
  illustrationNameLabel: "Illustration Name",
  illustrationNamePlaceholder: "Illustration Name",
  assetSelectorHeading: "Illustration",
  lightLabel: "Light",
  darkLabel: "Dark",
  variantSyncLabel: "Use same image for dark mode",
  variantSyncHelper: "Uncheck to upload a separate dark variant",
  saveButton: "Save",
  cancelButton: "Cancel",
  productCategoryEmptyError: "Product category cannot be empty",
  illustrationCategoryEmptyError: "Illustration category cannot be empty",
  illustrationFileEmptyError: "Illustration file cannot be empty",
  illustrationFileDarkEmptyError: "Dark illustration file cannot be empty",
  vpnRequiredMessage: `Please connect to ${process.env.REACT_APP_VPN_ENVIRONMENT} VPN`,
  darkroomUploadFailedError:
    "Darkroom upload failed, so the illustration was not added. Please try again. If the problem persists, log out, log back in and retry.",
  illustrationAddedTitle: "Illustration added",
  illustrationAddedContent: (combinedName) =>
    `${combinedName} has been added successfully`,
  illustrationAddedBcsSyncFailedContent: (combinedName) =>
    `${combinedName} was added, but BCS could not be activated for all app IDs. Open BCS Config to configure manually.`,
  illustrationAddedCta: "View",
};

function CreateDefaultIllustrationPage() {
  let history = useHistory();
  const location = useLocation();
  const canvasRef = useRef(null);

  // Context state
  const { user } = useContext(UserContext);

  // page loading state
  const [isLoading, setIsLoading] = useState(false);

  // Input value states
  const [productCategories, setproductCategories] = useState([]);
  const [illustrationCategories, setIllustrationCategories] = useState([]);
  const [selectedProductCategory, setSelectedProductCategory] = useState("");
  const [selectedIllustrationCategory, setSelectedIllustrationCategory] =
    useState("");
  const [illustrationName, setIllustrationName] = useState(null);
  const [illustrationFile, setIllustrationFile] = useState(null);
  const [variantSync, setVariantSync] = useState(true);
  const [illustrationFileDark, setIllustrationFileDark] = useState(null);

  // Error states
  const [productCategoryDropdownError, setProductCategoryDropdownError] =
    useState(null);
  const [
    illustrationCategoryDropdownError,
    setIllustrationCategoryDropdownError,
  ] = useState(null);
  const [illustrationNameError, setIllustrationNameError] = useState(null);
  const [assetSelectorError, setAssetSelectorError] = useState(null);
  const [assetSelectorDarkError, setAssetSelectorDarkError] = useState(null);
  const [vpnConnectionError, setVpnConnectionError] = useState(null);
  const [darkroomError, setDarkroomError] = useState(null);

  // Cancel button click
  const handeCancelClick = () => {
    //TODO : Check for any changes and give user a prompt
    history.goBack();
  };

  // Save Button click
  const handleSaveClick = async () => {
    // Check if Product Category is selected
    if (selectedProductCategory) {
      setProductCategoryDropdownError(null);
    } else {
      setProductCategoryDropdownError(texts.productCategoryEmptyError);
    }

    // Check if Illustration Category is selected
    if (selectedIllustrationCategory) {
      setIllustrationCategoryDropdownError(null);
    } else {
      setIllustrationCategoryDropdownError(
        texts.illustrationCategoryEmptyError
      );
    }

    // Check if illustration name passes validations
    if (validIllustrationName(illustrationName)) {
      setIllustrationNameError(null);
    } else {
      setIllustrationNameError(getIllustrationNameError(illustrationName));
    }

    //Check if illustration file is selected
    if (illustrationFile) {
      setAssetSelectorError(null);
    } else {
      setAssetSelectorError(texts.illustrationFileEmptyError);
    }

    // Check dark file when variant_sync is off
    if (!variantSync) {
      if (illustrationFileDark) {
        setAssetSelectorDarkError(null);
      } else {
        setAssetSelectorDarkError(texts.illustrationFileDarkEmptyError);
      }
    }

    if (
      selectedProductCategory &&
      selectedIllustrationCategory &&
      validIllustrationName(illustrationName) &&
      illustrationFile &&
      (variantSync || illustrationFileDark)
    ) {
      setIsLoading(true);
      setVpnConnectionError(null);
      setDarkroomError(null);
      try {
        const isUserConnectedToVpn = await checkVpnConnection();
        if (!isUserConnectedToVpn) {
          setIsLoading(false);
          setVpnConnectionError(texts.vpnRequiredMessage);
          return;
        }
        setVpnConnectionError(null);

        const name = {
          product_category: selectedProductCategory.key,
          illustration_category: selectedIllustrationCategory.key,
          illustration_name: illustrationName,
        };
        const combinedName = getCombinedName(name);
        // Fail fast on a duplicate name before uploading to Darkroom
        await isIllustrationNameUnique(combinedName);

        // Darkroom first: the asset is only created if the Darkroom upload
        // succeeds, so no asset can exist without a Darkroom URL
        let darkroomResponse;
        let darkroomResponseDark = null;
        try {
          const canvas = canvasRef.current;
          if (!canvas) throw new Error("Canvas not ready");
          const blob = await getRasterImageFromSvg(illustrationFile, canvas);
          darkroomResponse = await getDarkroomStorageUrls(combinedName);
          await uploadFileToBucket(
            darkroomResponse.data.data.upload_url,
            blob,
            user.idToken
          );
          if (!variantSync && illustrationFileDark) {
            const blobDark = await getRasterImageFromSvg(illustrationFileDark, canvas);
            darkroomResponseDark = await getDarkroomStorageUrls("dark_" + combinedName);
            await uploadFileToBucket(
              darkroomResponseDark.data.data.upload_url,
              blobDark,
              user.idToken
            );
          }
        } catch (err) {
          console.error(err);
          setIsLoading(false);
          setDarkroomError(texts.darkroomUploadFailedError);
          return;
        }

        const illustrationId = await createDefaultIllustration(
          name,
          illustrationFile,
          user.uid,
          darkroomResponse.data.data.public_url,
          variantSync,
          darkroomResponseDark?.data.data.public_url ?? null,
          illustrationFileDark
        );

        let bcsSyncFailed = false;
        try {
          const lightUrl = darkroomResponse.data.data.public_url;
          const darkUrl = variantSync
            ? lightUrl
            : (darkroomResponseDark?.data.data.public_url ?? lightUrl);
          await activateBcsForAllAppIds(
            combinedName.toUpperCase(),
            lightUrl,
            darkUrl,
            user.idToken
          );
        } catch (error) {
          console.error("BCS activation failed:", error);
          bcsSyncFailed = true;
        }

        setIsLoading(false);
        history.push(`/${routes.ILLUSTRATIONS}/${routes.ASSETS}`, {
          notification: {
            title: texts.illustrationAddedTitle,
            content: bcsSyncFailed
              ? texts.illustrationAddedBcsSyncFailedContent(combinedName)
              : texts.illustrationAddedContent(combinedName),
            ctaText: texts.illustrationAddedCta,
            illustrationId: illustrationId,
          },
        });
      } catch (error) {
        setIsLoading(false);
        if (error.name === "FirebaseError") {
          console.error(error.message);
        } else if (
          error.message === "Illustration with the same name already exists"
        ) {
          setIllustrationNameError(error.message);
        } else {
          console.error(error);
        }
      }
    }
  };

  useEffect(() => {
    getIllustrationNameComponentCategories()
      .then((querySnapshot) => {
        querySnapshot.forEach(function (doc) {
          if (doc.id === "product_category") {
            setproductCategories(
              arrayOfStringsToArrayOfIdKeyObjects(doc.data().values)
            );
          }
          if (doc.id === "illustration_category") {
            setIllustrationCategories(
              arrayOfStringsToArrayOfIdKeyObjects(doc.data().values)
            );
          }
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }, [setproductCategories, setIllustrationCategories, location]);

  return (
    <React.Fragment>
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <LoadingPage />
        </div>
      ) : null}
      <canvas style={{ display: "none" }} ref={canvasRef} />
      <div className={styles.createPageContainer}>
        <div className={styles.breadcrumbContainer}>
          <Breadcrumb>
            <Crumb
              text={texts.breadcrumbAssets}
              tagProps={{ to: "/" }}
              as={Link}
            />
            <Separator />
            <Crumb
              text={texts.breadcrumbAddIllustration}
              tagProps={{
                to: `/${routes.ILLUSTRATIONS}/${routes.ASSETS}/${routes.CREATE}`,
              }}
              as={Link}
            />
          </Breadcrumb>
        </div>
        <div>
          <h3 className={styles.heading}>{texts.pageHeading}</h3>
          <div className={styles.flagContainer}>
            <Flag
              page
              info
              title={texts.namingRulesTitle}
              content={texts.namingRulesContent}
            />
          </div>
          <div className={styles.formRow}>
            <div className={styles.productCategoryContainer}>
              <p className={styles.inputLabel}>{texts.productCategoryLabel}</p>
              <div className={styles.inputContainer}>
                <Dropdown
                  typeahead
                  items={productCategories}
                  onChange={(selectedProductCategory) => {
                    setSelectedProductCategory(selectedProductCategory);
                    setProductCategoryDropdownError(null);
                  }}
                  stretch
                  invalid={!!productCategoryDropdownError}
                />
              </div>
              {productCategoryDropdownError ? (
                <Flag error title={productCategoryDropdownError} />
              ) : null}
            </div>
            <div className={styles.illustrationCategoryContainer}>
              <p className={styles.inputLabel}>
                {texts.illustrationCategoryLabel}
              </p>
              <div className={styles.inputContainer}>
                <Dropdown
                  items={illustrationCategories}
                  onChange={(selectedIllustrationCategory) => {
                    setSelectedIllustrationCategory(
                      selectedIllustrationCategory
                    );
                    setIllustrationCategoryDropdownError(null);
                  }}
                  stretch
                  invalid={!!illustrationCategoryDropdownError}
                />
              </div>
              {illustrationCategoryDropdownError ? (
                <Flag error title={illustrationCategoryDropdownError} />
              ) : null}
            </div>
            <div className={styles.illustrationNameContainer}>
              <p className={styles.inputLabel}>{texts.illustrationNameLabel}</p>
              <div className={styles.inputContainer}>
                <TextField
                  required={!!illustrationNameError}
                  size="m"
                  stretch
                  placeholder={texts.illustrationNamePlaceholder}
                  type="text"
                  invalid={!!illustrationNameError}
                  onChange={(e) => {
                    setIllustrationName(e.target.value);
                    setIllustrationNameError(null);
                  }}
                />
              </div>
              {illustrationNameError ? (
                <Flag error title={illustrationNameError} />
              ) : null}
            </div>
          </div>

          <div className={styles.illustrationSelectorContainer}>
            <div className={styles.inputContainer}>
              <AssetSelector
                heading={
                  <span>
                    {texts.assetSelectorHeading}{" "}
                    <span className={styles.modeChip}>{texts.lightLabel}</span>
                  </span>
                }
                onAssetSelected={(file) => {
                  if (file) {
                    setIllustrationFile(file);
                    setAssetSelectorError(null);
                  }
                }}
              />
            </div>
            {assetSelectorError ? (
              <Flag error title={assetSelectorError} />
            ) : null}

            <label className={styles.variantSyncRow}>
              <input
                type="checkbox"
                checked={variantSync}
                onChange={(e) => {
                  setVariantSync(e.target.checked);
                  if (e.target.checked) {
                    setIllustrationFileDark(null);
                    setAssetSelectorDarkError(null);
                  }
                }}
              />
              <div className={styles.variantSyncText}>
                <span className={styles.variantSyncLabel}>{texts.variantSyncLabel}</span>
                <span className={styles.variantSyncHelper}>{texts.variantSyncHelper}</span>
              </div>
            </label>

            {!variantSync && (
              <div className={styles.inputContainer}>
                <AssetSelector
                  heading={
                    <span>
                      {texts.assetSelectorHeading}{" "}
                      <span className={styles.modeChipDark}>{texts.darkLabel}</span>
                    </span>
                  }
                  darkBackground
                  onAssetSelected={(file) => {
                    if (file) {
                      setIllustrationFileDark(file);
                      setAssetSelectorDarkError(null);
                    }
                  }}
                />
                {assetSelectorDarkError ? (
                  <Flag error title={assetSelectorDarkError} />
                ) : null}
              </div>
            )}
          </div>

          {vpnConnectionError ? (
            <Flag error title={vpnConnectionError} />
          ) : null}
          {darkroomError ? <Flag error title={darkroomError} /> : null}
          <Stack>
            <Button primary onClick={handleSaveClick}>
              {texts.saveButton}
            </Button>
            <Button secondary onClick={handeCancelClick}>
              {texts.cancelButton}
            </Button>
          </Stack>
        </div>
      </div>
    </React.Fragment>
  );
}

export default CreateDefaultIllustrationPage;
